import { useState, type RefObject } from 'react';
import { IconChevronRight, IconEye } from '@tabler/icons-react';
import { Dropdown } from 'antd';
import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useUser } from '@/modules/auth/hooks';
import { useViewFilter } from '@/modules/secrets/hooks/useViewFilter';
import { assignedToMeViewFilter } from '@/modules/secrets/utilities/assignedToMeViewFilter';
import { deriveViewState } from '@/modules/secrets/utilities/deriveViewState';
import { Submenu } from '@/modules/ui';

import * as felaRules from './ShowRecordsMenu.rules';
import { item } from './UserMenu.rules';

interface ShowRecordsMenuProps {
    // The card popup's root, so the flyout portals inside it (keeps the parent
    // menu's outside-click detection from treating the flyout as "outside").
    containerRef: RefObject<HTMLDivElement | null>;
    onChosen: () => void;
}

const options = [
    { key: 'all', messageId: 'sidebar.userMenu.showRecords.all' },
    { key: 'assigned', messageId: 'sidebar.userMenu.showRecords.assignedToMe' },
] as const;

type Option = (typeof options)[number]['key'];

export const ShowRecordsMenu = ({ containerRef, onChosen }: ShowRecordsMenuProps) => {
    const [open, setOpen] = useState(false);
    const { css } = useFela();
    const intl = useIntl();
    const isMin768 = useBreakpoint('sm');
    const user = useUser();
    const { viewFilter, setView } = useViewFilter();

    const viewState = deriveViewState(viewFilter, user?.id);
    const activeOption = options.find(option => option.key === viewState);
    const selectionLabel = activeOption ? intl.formatMessage({ id: activeOption.messageId }) : null;

    const choose = (target: Option) => {
        if (target === 'all') {
            setView(null);
        } else if (user) {
            setView(assignedToMeViewFilter(user));
        }
        setOpen(false);
        onChosen();
    };

    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
            trigger={['hover', 'click']}
            // Desktop: flyout to the right of the row; phones: overlay anchored to the row's top-right corner
            align={isMin768 ? { points: ['tl', 'tr'], offset: [16, 0] } : { points: ['tr', 'tr'], offset: [0, 4] }}
            // On phones prevent the popup from stretching to the full-width trigger row
            styles={isMin768 ? undefined : { root: { minWidth: 200 } }}
            transitionName=''
            getPopupContainer={() => containerRef.current ?? document.body}
            popupRender={() => (
                <Submenu
                    options={options.map(option => ({
                        key: option.key,
                        label: <FormattedMessage id={option.messageId} />,
                    }))}
                    activeKey={viewState}
                    onSelect={choose}
                />
            )}
        >
            <button type='button' className={css(item)}>
                <IconEye size={16} />
                <span className={css(felaRules.itemLabel)}>
                    <FormattedMessage id='sidebar.userMenu.showRecords' />
                </span>
                {selectionLabel && <span className={css(felaRules.selectionLabel)}>{selectionLabel}</span>}
                <IconChevronRight size={16} className={css(felaRules.chevronRight)} />
            </button>
        </Dropdown>
    );
};
